<template>
  <section class="ugc-tray-wrapper-edit ugc-tray-wrapper-edit--flex">
    <input
      v-if="tmallEnabled && isMy"
      @click="setUGCImageTmall"
      :id="inputId"
      class="hide ugc-image-source"
      type="button"
      accept="image/jpg,image/jpeg,image/png"
      :name="inputId"
      :ref="inputId"
    />
    <input
      v-else
      @change="setUGCImage"
      :id="inputId"
      class="hide ugc-image-source"
      type="file"
      accept="image/jpg,image/jpeg,image/png"
      :name="inputId"
      :ref="inputId"
    />
    <button
      @click="onUploadClick"
      class="button ugc-tray-wrapper-button ugc-tray-wrapper-edit-apply-button"
      type="button"
    >
      <span class="text">
        {{ textLabel || translations[inputLabel] }}
      </span>
    </button>
    <button
      @click="edit"
      :disabled="disableButton"
      type="button"
      class="ugc-tray-wrapper-edit-container-button"
    >
      <span class="icon icon-camera"></span>
      <span class="text">{{ buttonText }}</span>
    </button>
  </section>
</template>

<script>
import ugc from '@/mixins/UGC';
import undo from "@/mixins/Customizer/Undo";
import imageUpload from "@/mixins/UGC/imageUpload";

import {getUGCDataByID} from '@/libs/graphics/UGCData';
import { fetchAndInsertImageToGraphicSettings } from "@/libs/graphics/GraphicImageAssetProcesser";

const defaultClickOverride = () => {};
export default {
  name: "UgcImageEdit",
  mixins: [ugc, undo, imageUpload],
  props: {
    clickOverride: {
      type: Function,
      default: defaultClickOverride,
    },
    disableButton: {
      type: Boolean,
      default: false
    },
    textLabel: {
      type: String,
      default: null
    },
    inputLabel: {
      type: String,
      default: "ugcUpload"
    },
    inputId: {
      type: String,
      default: "swatch-uploader-input"
    },
  },
  computed: {
    buttonText() {
      const { translations } = this;
      return this.isApparel ? translations.ugcReplace : translations.ugcEdit;
    },
    queryParams() {
      return this.$store.state.utils.queryParams;
    },
    tempData() {
      return this.$store.state.ugc.tempData.features[this.activeUGCIndex];
    },
    ugcFeature() {
      return this.tempData.ugcFeature;
    },
    shouldUseClickOverride() {
      return this.clickOverride && this.clickOverride !== defaultClickOverride;
    },
  },
  methods: {
    setDataLayer() {
      if (this.$store.getters['utils/useEcomDataLayerFormat']) {
        this.$ugcDataLayer.logAnalyticsEvent({
          action: 'ugc',
          type: this.$ugcDataLayer.types.clickUgctool,
          eventLabel: 'Replace'
        });
      }
    },
    clicked() {
      this.$store.commit("ugc/tempData/SET_ACTIVE_UGC_INDEX", "ugc");
      this.$store.commit('swatches/SET_SWATCHES_SELECTED', {
        component: this.activeComponent,
        data: { ...this.selectedSwatches[this.activeComponent], default: false },
      });

      this.savePreviousSwatch({ feature: this.$recipe.getUGCFeatureType(this.activeComponent) });

      if (this.$store.state.utils.configuration.enableFlyOutMenu) {
        this.updateStandardMenuForUGC();
      }

      this.reApplyUgcSwatch();
      this.resetGeometrybyUGCFeature();
      // Set onbeforeunload event
      this.$eventBus.$emit("set-onbeforeunload", true);
      // updates tabs
      this.$eventBus.$emit("block-features-by-swatch-id");
    },
    /**
     * @function edit
     * Load ugc data in tempData/recipe from storageController/recipes using
     * the buckets size selected in ugc.
     * If storageController doesn't have the bucketSize it returns a warm
     */
    edit() {
      this.$store.commit("ugc/tempData/SET_ACTIVE_UGC_INDEX", "ugc");
      let activeComponent = this.$store.state.standardNavigation.activeComponent;
      this.$store.commit('ugc/tempData/SET_ACTIVE_UGC_COMPONENT', activeComponent);
      if (this.shouldUseClickOverride) {
        this.clickOverride();
        return;
      }
      // tracks eventAction "Customizer - Create your own" / edit
      this.setDataLayer();
      const { tempData } = this.$store.state.ugc;
      const { activeUGCComponent: componentName } = tempData.features[tempData.activeUGCIndex];
      const bucketSize = this.returnBucketSize(componentName);
      const { recipes } = this.$store.state.ugc.storageController;

      if (recipes?.[bucketSize]?.[componentName]) {
        this.$store.commit("ugc/tempData/SET_RECIPE", this.recipe_v2);
        this.$store.commit("ugc/tempData/ADD_UGC_COMPONENT", {
          componentName,
          defaultUGCSwatchId: this.defaultRecipe.components[componentName].swatch_id,
          ugc: recipes[bucketSize][componentName],
          replaceUgc: true,
          uploadedAnother: this.$store.getters['ugc/tempData/uploadedAnotherImage'],
        });
      } else {
        console.warn(`Unable to get ugc object for ${componentName}`);
        return;
      }
      let { ugc: ugcObject } = this.tempRecipe.components[componentName];
      this.$store.commit("ugc/tempData/SET_UGC_SIZE", ugcObject.size);
      const { image_id, image_url } = this.ugcFeature ? ugcObject.placements[0] : ugcObject.original_image;
      const fileFormat = image_url.split(".").pop();

      this.$store.commit("ugc/tempData/SET_UGC_IMAGE", {
        imagePath: image_url,
        temp_image_id: image_id,
        imageFormat: fileFormat
      });

      this.$store.dispatch("ugc/SAVE_ASSETS_3D")
        .then(() => {
          this.$store.commit("ugc/SET_UGC_VIEWS");
          this.$modal.show(this.$store.state.utils.configuration.enableNewUgcModal ? "newUgcModal" : "ugcModal");
          this.$eventBus.$emit("image-finished-loading");
          this.$emit("image-loaded", true);
          this.$store.commit("ugc/stateManager/SET_LOADING", false);
        });
    },
    /**
     * @deprecated
     * @function setClickUGC
     * Validate if the component is already set into the recipe and show edit functionality
     * instead of apply the ugc image into the 3d model
     */
    setClickUGC() {
      this.$store.commit("ugc/tempData/SET_ACTIVE_UGC_INDEX", "ugc");
      let activeComponent = this.$store.state.legacyNavigation.activeComponent;
      this.$store.commit('ugc/tempData/SET_ACTIVE_UGC_COMPONENT', activeComponent);
      if (this.isApparel) {
        activeComponent = this.$store.state.standardNavigation.activeSubComponent;
        this.reApplyUGCImage();
      } else {
        if (this.shouldUseClickOverride) {
          this.clickOverride();
        }
        const component = this.$recipe.getUGCFeatureType(activeComponent);
        if (component === "ugc") {
          this.edit();
        } else {
          // tracks eventAction "Customizer - Create your own" / apply
          this.setDataLayer();
          this.clicked();
        }
      }

    },
    async reApplyUGCImage(){
      const activeComponent = this.$store.state.standardNavigation.activeComponent;
      const activeSubComponent = this.$store.state.standardNavigation.activeSubComponent;
      const ugcDataStore = getUGCDataByID(activeComponent, activeSubComponent);
      const subComponentData = this.$store.state.recipe.recipe_v2
        .components[activeComponent]?.sub_components[activeSubComponent];
      const { tempData } = this.$store.state.ugc;
      const imgId = tempData.features[tempData.activeUGCIndex].ugc_image_id ||
        subComponentData?.ugc?.original_image.image_id || ugcDataStore?.ugcData.imgId;

      if (ugcDataStore) {
        await fetchAndInsertImageToGraphicSettings({
          src: ugcDataStore.ugcData.href,
          imgUrl: ugcDataStore.ugcData.original_image,
          imgId,
          componentID: activeComponent,
          subcomponentID: activeSubComponent,
          width: ugcDataStore.ugcData.width,
          height: ugcDataStore.ugcData.height,
          type: 'ugc',
        })
        this.$store.dispatch('buyPanel/GET_PRICE', { recipe: this.recipe_v2 });
        this.$store.commit('graphicsController/SET_EDITOR_VISIBLE', true);
        if (this.isMobile)
          this.$store.commit('standardNavigation/IS_STANDARD_MENU_OPEN', false);
        this.$store.dispatch('swatches/GET_RELATION_SWATCH_BY_FEATURE_ID', 'ugc')
          .then(({ swatches_not_allowed }) => {
            this.$store.commit(
              'swatches/SET_DISABLED_SWATCHES_BY_FEATURE',
              { swatches: swatches_not_allowed, feature: 'ugc' },
            );
          });
      }
    }
  },
  created() {
    this.$eventBus.$on("edit-ugc", this.edit);
  },
  beforeDestroy() {
    this.$eventBus.$off("edit-ugc", this.edit);
  }
};
</script>
